// src/components/ChannelAnalyzerTool.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './ChannelAnalyzer.css';
import { event } from '../utils/gtm';
import UpgradeError from './UpgradeError';
import InfoTooltip from './InfoTooltip';

const timeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  const daysPassed = Math.floor(seconds / 86400);

  let text = "Today";
  if (daysPassed > 365) {
    text = `${Math.floor(daysPassed / 365)} years ago`;
  } else if (daysPassed > 30) {
    text = `${Math.floor(daysPassed / 30)} months ago`;
  } else if (daysPassed > 0) {
    text = `${daysPassed} days ago`;
  }

  return { text };
};

// A small, reusable component for displaying video cards
const VideoCard = ({ video }) => {
    const formatCompact = (num) => Intl.NumberFormat('en-US', { notation: 'compact' }).format(num);
    const ageInfo = timeAgo(video.published_at);
  
    return (
      <div className="video-card">
        <a href={`https://www.youtube.com/watch?v=${video.id}`} target="_blank" rel="noopener noreferrer">
          <img src={video.thumbnail_url} alt="Video thumbnail" className="video-card-thumbnail" />
        </a>
        <div className="video-card-info">
          <h5 className="video-card-title">{video.title}</h5>
          <div className="video-card-footer">
              <div className="video-card-stats">
                  <div className="stat-item">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.432 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    <span>Views: {formatCompact(video.view_count)}</span>
                  </div>
                  <div className="stat-item serp-vph">
                    <span className="font-bold">Avg. VPH: {video.vph}</span>
                    <InfoTooltip content="<strong>Avg. VPH (Lifetime):</strong><br />This is the average Views Per Hour over the video's lifetime, calculated by dividing total views by the hours since publication." />
                  </div>
              </div>
              <span className="video-card-age font-semibold">{ageInfo.text}</span>
          </div>
        </div>
      </div>
    );
  };

const ChannelAnalyzerTool = ({
    user,
    channelInput,
    setChannelInput,
    analysisResult,
    isLoading,
    error,
    handleAnalyzeChannel,
    addCompetitor,
    removeCompetitor,
    competitors,
    onCompetitorClick,
    actionCosts
}) => {
  const navigate = useNavigate();
  const formatLarge = (num) => Intl.NumberFormat().format(num);
  const isAlreadyAdded = analysisResult ? competitors.some(c => c.id === analysisResult.channel_stats.channel_id) : false;

  const competitorLimit = user.plan === 'pro' ? 10 : 3;
  const hasReachedLimit = competitors.length >= competitorLimit;
  const canAddCompetitor = !isAlreadyAdded && !hasReachedLimit;

  return (
    <div className="tool-container channel-analyzer-container">
      <header className="tool-header">
        <h1>Channel Analyzer</h1>
        <p>Uncover the winning strategy of any channel in your niche.</p>
      </header>
      
      <div className="tracked-channels-section">
        <h3>Your Tracked Competitors</h3>
        <div className="tracked-channels-list">
          {competitors && competitors.length > 0 ? (
            competitors.map(comp => (
              <div key={comp.id} className="competitor-card" onClick={() => onCompetitorClick(comp)}>
                <img src={comp.thumbnail_url} alt={comp.title} />
                <span>{comp.title}</span>
                <button 
                  className="delete-competitor-btn" 
                  onClick={(e) => { e.stopPropagation(); removeCompetitor(comp.id); }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              </div>
            ))
          ) : (
            <p className="no-competitors-message">No channels tracked yet. Analyze a channel and click the star to add it.</p>
          )}
        </div>
      </div>

      <div className="search-container">
        <div className="pill-search-bar">
          <input
            className="pill-search-input"
            type="text"
            value={channelInput}
            onChange={(e) => setChannelInput(e.target.value)}
            placeholder="Enter channel URL, handle (@handle), or ID"
            onKeyDown={(e) => e.key === 'Enter' && handleAnalyzeChannel()}
          />
          <button className="pill-search-button" onClick={() => handleAnalyzeChannel()} disabled={isLoading}>
            {isLoading ? (
              <div className="button-spinner-light"></div>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="search-icon">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                </svg>
                <div className="cost-badge">
                  <span>{actionCosts.analyze_channel}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" role="img" aria-label="credits: stacked coins"><defs><linearGradient id="coinFace" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#fef08a"/><stop offset="100%" stopColor="#facc15"/></linearGradient><linearGradient id="coinEdge" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#eab308"/><stop offset="100%" stopColor="#ca8a04"/></linearGradient></defs><ellipse cx="32" cy="48" rx="20" ry="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="38" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="38" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="28" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="28" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/><rect x="12" y="18" width="40" height="10" rx="6" fill="url(#coinEdge)" stroke="#92400e" strokeWidth="2"/><ellipse cx="32" cy="18" rx="20" ry="6" fill="url(#coinFace)" stroke="#92400e" strokeWidth="2"/></svg>
                </div>
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        error.toLowerCase().includes("credits") ?
        <UpgradeError message={error} source="Channel Analyzer" /> :
        <p className="error-message">{error}</p> // Keep for non-credit errors
      )}
      
      {analysisResult && (
        <div className="channel-results-container">
          {/* Channel Header Stats */}
          <div className="channel-header">
            <img src={analysisResult.channel_stats.thumbnail_url} alt="Channel thumbnail" className="channel-thumbnail" />
            <div className="channel-info">
              <div className="channel-title-bar">
                <h2>{analysisResult.channel_stats.title}</h2>
                <button
                  className={`add-competitor-btn ${hasReachedLimit && !isAlreadyAdded ? 'is-limit-reached' : ''}`}
                  onClick={(e) => { e.stopPropagation(); // Keep stopPropagation
                    event({ action: 'manage_item', category: 'Engagement', label: 'Channel Analyzer: Add Competitor' });
                    addCompetitor(analysisResult.channel_stats);
                  }}
                  disabled={!canAddCompetitor}
                  title={isAlreadyAdded ? "Already Tracked" : (hasReachedLimit ? `Competitor limit reached (${competitorLimit})` : "Add to Tracking List")}
                >
                  {/* Star Icon */}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                    <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.007z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <div className="channel-stats-grid">
                <div className="channel-stat-item">
                  <span className="stat-value">{formatLarge(analysisResult.channel_stats.subscriber_count)}</span>
                  <span className="stat-label">
                    Subscribers
                    <InfoTooltip content="The total number of subscribers for this channel, updated daily." />
                  </span>
                </div>
                <div className="channel-stat-item">
                  <span className="stat-value">{formatLarge(analysisResult.channel_stats.view_count)}</span>
                  <span className="stat-label">
                    Total Views
                    <InfoTooltip content="The total number of public video views on this channel." />
                  </span>
                </div>
                <div className="channel-stat-item">
                  <span className="stat-value">{formatLarge(analysisResult.channel_stats.video_count)}</span>
                  <span className="stat-label">
                    Videos
                    <InfoTooltip content="The total number of public videos on this channel." />
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Video Lists */}
          <div className="results-grid">
            {user.plan === 'pro' ? (
              <div className="video-list-container">
                <h3>
                  Top Performing Videos
                  <InfoTooltip content={"<strong>How to Use This Data:</strong><br /><br />This list shows you the channel's \"greatest hits\"—the videos that have resonated most with their audience over time.<br /><br />Look for patterns and common themes. These are the channel's <strong>content pillars</strong>. A video on one of these proven topics is far more likely to succeed than a random idea. This is your roadmap to what works in this niche."} />
                </h3>
                <div className="tag-trends-container">
                  {analysisResult.tag_trends.top && analysisResult.tag_trends.top.map(t => <span key={t.tag} className="trend-tag-pill">{t.tag} ({t.count})</span>)}
                </div>
                <div className="video-list">
                  {analysisResult.top_videos.map(video => <VideoCard key={video.id} video={video} />)}
                </div>
              </div>
            ) : (
              <div className="video-list-container locked-feature-wrapper">
                <h3>Top Performing Videos <span className="pro-badge">PRO</span></h3>
                <div className="locked-content-overlay">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="locked-icon"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 00-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                    <h4>Unlock Top Video Analysis</h4>
                    <p>Upgrade to Pro to see a channel's best performing videos and tag trends.</p>
                    <button onClick={() => {
                      event({ action: 'click_upgrade_intent', category: 'Monetization', label: 'Source: Channel Analyzer Top Videos' });
                      navigate('/pricing'); 
                    }} className="rounded-full bg-gradient-to-r from-[#1d94d0] to-[#1E3A8A] px-5 py-2 text-sm font-semibold text-white transition-all duration-300 hover:shadow-lg hover:shadow-[#1d94d0]/40 hover:brightness-110">Upgrade to Pro</button>
                </div>
                <div className="tag-trends-container placeholder-content">
                  <span className="trend-tag-pill">popular topic</span>
                  <span className="trend-tag-pill">viral keyword</span>
                  <span className="trend-tag-pill">another trend</span>
                  <span className="trend-tag-pill">high-demand</span>
                </div>
                <div className="video-list placeholder-content">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="video-card">
                      <div className="video-card-thumbnail-placeholder"></div>
                      <div className="video-card-info">
                        <div className="video-card-title-placeholder"></div>
                        <div className="video-card-footer-placeholder"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="video-list-container">
              <h3>
                Recent Uploads
                <InfoTooltip content={"<strong>How to Use This Data:</strong><br /><br />This list shows you what the creator is focused on <em>right now</em>. It's a real-time glimpse into their current strategy and experiments.<br /><br />Pay close attention to the <strong>Age</strong> and <strong>Avg. VPH</strong>. A brand new video with a high Avg. VPH is a strong signal that the YouTube algorithm is actively promoting this topic. This is a potential trend you can jump on."} />
              </h3>
              <div className="tag-trends-container">
                {analysisResult.tag_trends.recent.map(t => <span key={t.tag} className="trend-tag-pill">{t.tag} ({t.count})</span>)}
              </div>
              <div className="video-list">
                {analysisResult.recent_videos.map(video => <VideoCard key={video.id} video={video} />)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChannelAnalyzerTool;